import React, { useRef, useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Plus, Minus, Save, FolderOpen } from 'lucide-react';
import { Prompt, PromptMetadata, PromptVariable } from '../types';
import { GitHubPromptManager } from '../utils/github';
import PromptTextEditor from './PromptTextEditor';
import VariableValidationAlerts from './VariableValidationAlerts';
import { validatePromptVariables } from '../utils/variables';
import { attemptSave, DEFAULT_SAVE_ERROR, type SaveGate } from '../utils/promptSave';

interface NewPromptPageProps {
  manager: GitHubPromptManager;
  onBack: () => void;
  onSave: (prompt: Prompt) => void | Promise<void>;
}

export const NewPromptPage: React.FC<NewPromptPageProps> = ({ manager, onBack, onSave }) => {
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>(['']);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userPrompt, setUserPrompt] = useState('');
  const [variables, setVariables] = useState<PromptVariable[]>([]);
  
  // UI state
  const [existingCategories, setExistingCategories] = useState<string[]>([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const saveGateRef = useRef<SaveGate>({ busy: false });

  const variableValidation = useMemo(
    () =>
      validatePromptVariables(
        [systemPrompt, userPrompt],
        variables.filter((v) => v.name.trim().length > 0)
      ),
    [systemPrompt, userPrompt, variables]
  );

  // Load existing categories for suggestions
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const prompts = await manager.listPrompts();
        const categories = [...new Set(prompts.map(p => {
          // Extract category from path (everything before the last slash)
          const parts = p.name.split('/');
          return parts.slice(0, -1).join('/');
        }))].filter(Boolean).sort();
        setExistingCategories(categories);
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    loadCategories();
  }, [manager]);

  const handleTagChange = (index: number, value: string) => {
    const newTags = [...tags];
    newTags[index] = value;
    setTags(newTags);
  };

  const addTag = () => {
    setTags([...tags, '']);
  };

  const removeTag = (index: number) => {
    if (tags.length > 1) {
      setTags(tags.filter((_, i) => i !== index));
    }
  };

  const generateId = (title: string) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'untitled-prompt';
  };

  const canSave = () => {
    // Minimal requirements for saving - just need some content or title
    return title.trim() || userPrompt.trim() || description.trim();
  };

  const handleSave = async () => {
    if (!canSave()) {
      alert('Please add at least a title, description, or user prompt before saving');
      return;
    }

    if (saveGateRef.current.busy || isSaving) {
      return;
    }

    const now = new Date().toISOString();
    const promptId = generateId(title || 'untitled-prompt');
    const promptCategory = category.trim() || 'drafts';

    const metadata: PromptMetadata = {
      id: promptId,
      title: title.trim() || 'Untitled Prompt',
      description: description.trim() || undefined,
      category: promptCategory,
      tags: tags.filter(tag => tag.trim()).map(tag => tag.trim()),
      version: '1.0.0',
      created: now,
      updated: now,
      author: 'User' // This could be pulled from GitHub user info
    };

    const prompt: Prompt = {
      metadata,
      prompts: {
        system: systemPrompt.trim() || undefined,
        user: userPrompt.trim() || 'Prompt content not yet added'
      },
      variables: (() => {
        const cleaned = variables
          .map((v) => ({ ...v, name: v.name.trim() }))
          .filter((v) => v.name.length > 0);
        return cleaned.length > 0 ? cleaned : undefined;
      })()
    };

    setSaveError(null);
    setIsSaving(true);
    const result = await attemptSave(saveGateRef.current, () => Promise.resolve(onSave(prompt)));
    setIsSaving(false);

    if (result.outcome === 'success') {
      setLastSaved(result.savedAt);
      return;
    }

    if (result.outcome === 'error') {
      console.error('Failed to save prompt:', result.message);
      setSaveError(result.message || DEFAULT_SAVE_ERROR);
    }
  };

  const suggestedCategories = [
    'analysis',
    'development', 
    'security',
    'infrastructure',
    'design',
    'testing',
    'database',
    'drafts'
  ];

  const filteredSuggestions = category 
    ? [...existingCategories, ...suggestedCategories].filter(cat => 
        cat.toLowerCase().includes(category.toLowerCase())
      ).slice(0, 8)
    : [...existingCategories, ...suggestedCategories].slice(0, 8);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ArrowLeft size={20} />
                Back to Library
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Create New Prompt</h1>
            </div>
            
            <div className="flex items-center gap-3">
              {lastSaved && !saveError && (
                <span className="text-sm text-gray-500">
                  Last saved {lastSaved.toLocaleTimeString()}
                </span>
              )}
              <button
                onClick={handleSave}
                disabled={!canSave() || isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save size={16} />
                {isSaving ? 'Saving...' : 'Save Prompt'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-8 space-y-8">
            {/* Basic Information Section */}
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                Basic Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter a descriptive title"
                  />
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                    <span className="text-gray-500 text-xs ml-1">(determines folder structure)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      onFocus={() => setShowCategoryDropdown(true)}
                      onBlur={() => setTimeout(() => setShowCategoryDropdown(false), 200)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                      placeholder="e.g., development, analysis, security"
                    />
                    <FolderOpen 
                      size={16} 
                      className="absolute right-3 top-3 text-gray-400"
                    />
                    
                    {showCategoryDropdown && filteredSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {filteredSuggestions.map((cat, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => {
                              setCategory(cat);
                              setShowCategoryDropdown(false);
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 text-sm"
                          >
                            <div className="flex items-center gap-2">
                              <FolderOpen size={14} className="text-gray-400" />
                              {cat}
                              {existingCategories.includes(cat) && (
                                <span className="text-xs text-gray-500">(existing)</span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Describe what this prompt does and when to use it"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <div className="space-y-2">
                  {tags.map((tag, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={tag}
                        onChange={(e) => handleTagChange(index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter a tag"
                      />
                      <button
                        type="button"
                        onClick={() => removeTag(index)}
                        disabled={tags.length === 1}
                        className="px-3 py-2 text-red-600 hover:text-red-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                      >
                        <Minus size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addTag}
                    className="flex items-center gap-1 px-3 py-2 text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    <Plus size={16} />
                    Add Tag
                  </button>
                </div>
              </div>
            </div>

            {/* Prompt Content Section */}
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                Prompt Content
              </h2>

              <p className="text-sm text-gray-600">
                Reference variables inline with{' '}
                <code className="bg-gray-100 px-1 rounded">{'${VariableName}'}</code>. See{' '}
                <span className="text-gray-500">docs/VARIABLE_SYNTAX.md</span> for the full guide.
              </p>

              <VariableValidationAlerts validation={variableValidation} compact />

              <PromptTextEditor
                label="System Prompt"
                hint="optional"
                value={systemPrompt}
                onChange={setSystemPrompt}
                rows={6}
                placeholder="System instructions… e.g. You are a ${Role} assistant."
                variables={variables}
                insertableNames={variables.map((v) => v.name)}
              />

              <PromptTextEditor
                label="User Prompt"
                hint="main prompt content"
                value={userPrompt}
                onChange={setUserPrompt}
                rows={8}
                placeholder="Enter the main prompt. Use ${VariableName} for dynamic content…"
                variables={variables}
                insertableNames={variables.map((v) => v.name)}
              />
            </div>

            {/* Variables metadata */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                <h2 className="text-xl font-semibold text-gray-900">Variables</h2>
                <button
                  type="button"
                  onClick={() =>
                    setVariables([
                      ...variables,
                      { name: '', type: 'string', required: false },
                    ])
                  }
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <Plus size={16} />
                  Add Variable
                </button>
              </div>
              <p className="text-sm text-gray-500">
                Optional metadata for {'${Variable}'} references. Names should match tokens in the
                prompt text.
              </p>
              {variables.length === 0 ? (
                <p className="text-sm text-gray-400">No variables defined yet.</p>
              ) : (
                <div className="space-y-3">
                  {variables.map((variable, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-start border border-gray-200 rounded-md p-3"
                    >
                      <input
                        type="text"
                        value={variable.name}
                        onChange={(e) => {
                          const next = [...variables];
                          next[index] = { ...next[index], name: e.target.value };
                          setVariables(next);
                        }}
                        placeholder="Name"
                        className="px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <select
                        value={variable.type}
                        onChange={(e) => {
                          const next = [...variables];
                          next[index] = {
                            ...next[index],
                            type: e.target.value as PromptVariable['type'],
                          };
                          setVariables(next);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="string">string</option>
                        <option value="number">number</option>
                        <option value="boolean">boolean</option>
                        <option value="enum">enum</option>
                        <option value="range">range</option>
                      </select>
                      <label className="flex items-center gap-2 text-sm text-gray-700 px-1 py-2">
                        <input
                          type="checkbox"
                          checked={variable.required}
                          onChange={(e) => {
                            const next = [...variables];
                            next[index] = { ...next[index], required: e.target.checked };
                            setVariables(next);
                          }}
                        />
                        Required
                      </label>
                      <button
                        type="button"
                        onClick={() => setVariables(variables.filter((_, i) => i !== index))}
                        className="justify-self-start sm:justify-self-end px-3 py-2 text-red-600 hover:text-red-800 transition-colors"
                      >
                        <Minus size={16} />
                      </button>
                      <input
                        type="text"
                        value={variable.description || ''}
                        onChange={(e) => {
                          const next = [...variables];
                          next[index] = {
                            ...next[index],
                            description: e.target.value || undefined,
                          };
                          setVariables(next);
                        }}
                        placeholder="Description (optional)"
                        className="sm:col-span-2 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        value={
                          variable.default !== undefined && variable.default !== null
                            ? String(variable.default)
                            : ''
                        }
                        onChange={(e) => {
                          const next = [...variables];
                          next[index] = {
                            ...next[index],
                            default: e.target.value === '' ? undefined : e.target.value,
                          };
                          setVariables(next);
                        }}
                        placeholder="Default (optional)"
                        className="sm:col-span-2 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {variable.type === 'enum' && (
                        <input
                          type="text"
                          value={(variable.options || []).join(', ')}
                          onChange={(e) => {
                            const next = [...variables];
                            next[index] = {
                              ...next[index],
                              options: e.target.value
                                .split(',')
                                .map((s) => s.trim())
                                .filter(Boolean),
                            };
                            setVariables(next);
                          }}
                          placeholder="Enum options (comma-separated)"
                          className="sm:col-span-4 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Save Section */}
            <div className="pt-6 border-t border-gray-200 space-y-3">
              {saveError && (
                <div
                  role="alert"
                  className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                >
                  <span>{saveError}</span>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="shrink-0 font-medium text-red-700 underline hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Retry save
                  </button>
                </div>
              )}
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  {canSave() ? (
                    <span className="text-green-600">✓ Ready to save</span>
                  ) : (
                    <span className="text-gray-400">Add some content to enable saving</span>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={onBack}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={!canSave() || isSaving}
                    className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Save size={16} />
                    {isSaving ? 'Saving...' : 'Save Prompt'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};